import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ParentStorableData} from 'constants/SampleConstants';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import ChangeParentStorableButton from 'samples/storables/ChangeParentStorableButton';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {LocationGraphQL} from '../../../graphql-fragments/inventory/__generated/LocationGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import waitForEntrySaved from '../utils/waitForEntrySaved';
import * as AutomationFileDAO from './AutomationFileDAO';

interface AutomationOutputFileLocationPickerProps {
  runId: string | null;
  automationOutputFile: AutomationOutputFileGraphQL;
  locked: boolean;
  updateAutomationOutputFile: (automationOutputFile: AutomationOutputFileGraphQL) => void;
  storableSchema: StorableSchema | null;
  location: LocationGraphQL | null;
}

interface ConnectedProps {
  outputFileStatus: AutomationFileStatusGraphQL | null;
}

class AutomationOutputFileLocationPickerConnector extends DataConnector<
  AutomationOutputFileLocationPickerProps,
  ConnectedProps
> {
  enhanceProps(): ConnectedProps {
    return {
      outputFileStatus: this.props.automationOutputFile.id
        ? this.reduxState().ui.automationFileStatus[this.props.automationOutputFile.id]
        : null,
    };
  }
}

const AutomationOutputFileLocationPicker = (
  props: AutomationOutputFileLocationPickerProps & ConnectedProps
): JSX.Element | null => {
  const automationOutputFile = props.automationOutputFile;
  const locationIdFromFile = automationOutputFile.locationId || null;
  const locationIdFromStore = props.location?.id || null;
  const isLoading = locationIdFromFile !== locationIdFromStore;
  const buttonText = isLoading ? 'Loading...' : props.location ? props.location.name : 'Choose Location';
  const isWritable =
    !props.locked &&
    props.outputFileStatus !== AutomationFileStatusGraphQL.running &&
    automationOutputFile.status === AutomationFileStatusGraphQL.pending;
  return (
    <Row className="u-mini-spacing-top" bufferRowReset={true}>
      <Col md={3}>
        <span className="fieldLabel-sublabel">Location</span>
      </Col>
      <Col md={9}>
        <EditorPropertiesContext.Consumer>
          {({fileId}) => (
            <ChangeParentStorableButton
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              buttonSize={ButtonSize.MINI}
              className={css(locationPickerStyles.changeParentStorableButton)}
              icon={isLoading ? ModelTypeConstants.LOCATION : undefined}
              buttonText={buttonText}
              fullWidth={true}
              isWritable={isWritable}
              onSetParentStorable={async (data: ParentStorableData) => {
                const saved = fileId != null ? await waitForEntrySaved(fileId) : true;
                if (saved) {
                  const response = await AutomationFileDAO.updateAutomationOutputFileParams(
                    automationOutputFile.id,
                    {locationId: data.id!}
                  );
                  props.updateAutomationOutputFile(response.automationOutputFile);
                }
              }}
              storableSchema={props.storableSchema}
              titleText="Create new inventory in..."
              parentStorableTypesOverride={[ModelTypeConstants.LOCATION]}
            />
          )}
        </EditorPropertiesContext.Consumer>
      </Col>
    </Row>
  );
};

export const locationPickerStyles = StyleSheet.create({
  changeParentStorableButton: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export default AutomationOutputFileLocationPickerConnector.connect(AutomationOutputFileLocationPicker);
